import { useEffect } from 'react'
import { ChevronDown, UserX } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { EssentialMetadata } from './EssentialMetadata'
import { ProFeatureGate } from './ProFeatureGate'
import { cn } from '@/lib/utils'
import { PLACEHOLDER_VISITOR_METADATA } from './placeholderData'
import { useTranslation } from '@/hooks/useTranslation'

interface ChatConversation {
	id: string
	visitor_name?: string
	visitor_email?: string
	visitor_metadata?: any
	visitor_context_restricted?: boolean
	status: 'open' | 'closed'
	[key: string]: any
}

interface VisitorContextDrawerProps {
	conversation?: ChatConversation
	isOpen: boolean
	onToggle: () => void
	isPro: boolean
}

export function VisitorContextDrawer({
	conversation,
	isOpen,
	onToggle,
	isPro
}: VisitorContextDrawerProps) {
	const { t } = useTranslation()
	const navigate = useNavigate()

	// Prevent body scroll when drawer is open
	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = 'hidden'
		} else {
			document.body.style.overflow = ''
		}

		return () => {
			document.body.style.overflow = ''
		}
	}, [isOpen])

	const metadata = conversation?.visitor_metadata
	const isRestricted = conversation?.visitor_context_restricted === true
	const hasMetadata = metadata && Object.keys(metadata).length > 0

	// Determine what to show:
	// 1. Restricted: Show blurred placeholders (free user with data)
	// 2. Has metadata: Show real data (pro user)
	// 3. Empty: Show empty state (no data captured)
	const showRestricted = isRestricted && !hasMetadata
	const showRealData = hasMetadata && !isRestricted
	const showEmpty = !hasMetadata && !isRestricted

	// Use placeholder data when restricted
	const displayMetadata = showRestricted ? PLACEHOLDER_VISITOR_METADATA : metadata
	const displayVisitorName = showRestricted ? PLACEHOLDER_VISITOR_METADATA.name : conversation?.visitor_name
	const displayVisitorEmail = showRestricted ? PLACEHOLDER_VISITOR_METADATA.email : conversation?.visitor_email

	return (
		<>
			{/* Backdrop */}
			{isOpen && (
				<div
					className="fixed inset-0 bg-black/50 z-40 animate-in fade-in duration-200"
					onClick={onToggle}
					aria-hidden="true"
				/>
			)}

			{/* Drawer */}
			<div
				role="dialog"
				aria-label={t('visitorContextDrawer.visitorInformation')}
				aria-modal="true"
				className={cn(
					"fixed bottom-0 left-0 right-0 z-50",
					"bg-background rounded-t-2xl shadow-2xl",
					"max-h-[80vh] flex flex-col",
					"transition-transform duration-300 ease-out",
					isOpen ? "translate-y-0" : "translate-y-full"
				)}
			>
				<div className="flex items-center justify-center pt-3 pb-2">
					<div
						className="w-10 h-1 bg-muted-foreground/20 rounded-full cursor-grab active:cursor-grabbing"
						aria-hidden="true"
					/>
				</div>

				<div className="flex items-center justify-between px-4 py-2 border-b">
					<h2 className="text-sm font-semibold text-foreground">
						{t('visitorContextDrawer.visitorContext')}
					</h2>
					<Button
						variant="ghost"
						size="icon"
						className="h-8 w-8 hover:bg-muted"
						onClick={onToggle}
						aria-label={t('visitorContextDrawer.closeVisitorInformation')}
					>
						<ChevronDown className="h-4 w-4" />
					</Button>
				</div>

				{/* Content */}
				<ScrollArea className="flex-1 overscroll-contain">
					<div className="p-3 space-y-3 pb-8">
						{showEmpty ? (
							<EmptyState />
						) : (
							<>
								{/* Show upgrade prompt at the top for restricted users */}
								{showRestricted && (
									<ProFeatureGate onUpgradeClick={() => {
										onToggle()
										navigate('/settings?tab=billing')
									}} />
								)}

								<EssentialMetadata
									metadata={displayMetadata}
									isPro={isPro}
									isRestricted={showRestricted}
									visitorName={displayVisitorName}
									visitorEmail={displayVisitorEmail}
								/>
							</>
						)}
					</div>
				</ScrollArea>
			</div>
		</>
	)
}

function EmptyState() {
	const { t } = useTranslation()
	return (
		<div className="text-center py-8 px-4">
			<div className="w-12 h-12 mx-auto mb-3 rounded-full bg-muted/50 flex items-center justify-center">
				<UserX className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
			</div>
			<p className="text-sm font-medium text-foreground mb-1">
				{t('visitorContextDrawer.noVisitorInformationAvailable')}
			</p>
			<p className="text-xs text-muted-foreground leading-relaxed">
				{t('visitorContextDrawer.contextWillBeCaptured')}
			</p>
		</div>
	)
}
